import { useState, type SubmitEventHandler } from 'react';
import { toast } from 'sonner';
import { isValidUrl } from '@stashmark/shared';
import { useCreateBookmarkMutation } from '@/api/bookmarks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

type Props = {
  onSuccess: () => void;
  onCancel: () => void;
};

export const AddBookmarkForm = ({ onSuccess, onCancel }: Props) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const [createBookmark, { isLoading }] = useCreateBookmarkMutation();

  const handleSubmit: SubmitEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    void (async () => {
      setValidationError(null);

      if (!url.trim()) {
        setValidationError('URL is required');
        return;
      }

      if (!isValidUrl(url)) {
        setValidationError('Please enter a valid URL');
        return;
      }

      try {
        await createBookmark({
          url: url.trim(),
          title: title.trim() || undefined,
          description: description.trim() || undefined,
        }).unwrap();

        setUrl('');
        setTitle('');
        setDescription('');
        toast.success('Bookmark added successfully');
        onSuccess();
      } catch (err) {
        toast.error('Failed to create bookmark', {
          description: err instanceof Error ? err.message : 'Please try again',
        });
      }
    })();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="url">
          URL <span className="text-destructive">*</span>
        </Label>
        <Input
          type="url"
          id="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Bookmark title (optional)"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description (optional)"
          rows={3}
        />
      </div>

      {validationError && (
        <p className="text-sm text-destructive" role="alert">
          {validationError}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Adding...' : 'Add Bookmark'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};
